import { useState, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { useClinicalProduction } from './useClinicalProduction';
import { buildClinicalProductionPdf } from '../buildClinicalProductionPdf';

export const productionStatus = {
  COMPLETED: 'COMPLETED',
  IN_PROGRESS: 'IN_PROGRESS',
  ALL: 'ALL',
};

export const productionStatusLabels = {
  [productionStatus.COMPLETED]: 'Concluídos',
  [productionStatus.IN_PROGRESS]: 'Em andamento',
  [productionStatus.ALL]: 'Todos',
};

export const groupByOptions = {
  PATIENT: 'PATIENT',
  DENTIST: 'DENTIST',
  PROCEDURE: 'PROCEDURE',
};

export const groupByLabels = {
  [groupByOptions.PATIENT]: 'Agrupar por paciente',
  [groupByOptions.DENTIST]: 'Agrupar por dentista',
  [groupByOptions.PROCEDURE]: 'Agrupar por procedimento',
};

const INITIAL_VISIBLE_ITEMS = 6;

const toInitials = (name) =>
  (name ?? '')
    .split(' ')
    .filter((part) => part.length > 2)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

const buildGroupKey = (item, groupBy) => {
  if (groupBy === groupByOptions.DENTIST) return item.dentistName ?? 'Sem dentista';
  if (groupBy === groupByOptions.PROCEDURE) return item.procedureName ?? 'Sem descrição';
  return item.patientName ?? 'Sem paciente';
};

const buildGroupSubtitle = (item, groupBy) => {
  if (groupBy === groupByOptions.DENTIST) return 'Dentista responsável';
  if (groupBy === groupByOptions.PROCEDURE) return 'Procedimento';
  return [item.treatmentName, item.treatmentStatus].filter(Boolean).join(' · ');
};

// A linha do procedimento mostra apenas a informação que o cabeçalho do grupo
// ainda não expõe, para não repetir o mesmo nome duas vezes.
const buildItemSubtitle = (item, groupBy) => {
  if (groupBy === groupByOptions.DENTIST) return item.patientName;
  if (groupBy === groupByOptions.PROCEDURE)
    return [item.patientName, item.dentistName].filter(Boolean).join(' · ');
  return item.dentistName;
};

// Orquestra a tela do relatório de produção clínica. O período e a situação são
// recortados pela API; o agrupamento é local, para trocar sem nova requisição.
export function useClinicalProductionScreen() {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedStatus, setSelectedStatus] = useState(productionStatus.ALL);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState('');
  const [groupBy, setGroupBy] = useState(groupByOptions.PATIENT);
  const [showAllItems, setShowAllItems] = useState(false);

  const { summary, procedures, isLoading, error, refetch } = useClinicalProduction({
    selectedDate,
    status: selectedStatus,
    professionalId: selectedProfessionalId,
  });

  // Trocar de período ou de situação traz outra lista, então a listagem volta a
  // mostrar apenas os primeiros procedimentos.
  const changeSelectedDate = useCallback((date) => {
    setSelectedDate(date);
    setShowAllItems(false);
  }, []);

  const changeSelectedStatus = useCallback((status) => {
    setSelectedStatus(status);
    setShowAllItems(false);
  }, []);

  const changeSelectedProfessionalId = useCallback((professionalId) => {
    setSelectedProfessionalId(professionalId);
    setShowAllItems(false);
  }, []);

  const groups = useMemo(() => {
    const groupsByKey = new Map();

    procedures.forEach((item) => {
      const key = buildGroupKey(item, groupBy);

      if (!groupsByKey.has(key)) {
        groupsByKey.set(key, {
          key,
          title: key,
          subtitle: buildGroupSubtitle(item, groupBy),
          initials: toInitials(key),
          total: 0,
          items: [],
        });
      }

      const group = groupsByKey.get(key);
      group.total += item.amount ?? 0;
      group.items.push({ ...item, subtitle: buildItemSubtitle(item, groupBy) });
    });

    return Array.from(groupsByKey.values()).sort((first, second) => second.total - first.total);
  }, [procedures, groupBy]);

  // Recorta os grupos até o limite de itens visíveis, sem quebrar um grupo ao
  // meio: a listagem inicial mostra apenas os primeiros procedimentos.
  const visibleGroups = useMemo(() => {
    if (showAllItems) return groups;

    const limitedGroups = [];
    let remainingItems = INITIAL_VISIBLE_ITEMS;

    groups.forEach((group) => {
      if (remainingItems <= 0) return;

      limitedGroups.push({ ...group, items: group.items.slice(0, remainingItems) });
      remainingItems -= group.items.length;
    });

    return limitedGroups;
  }, [groups, showAllItems]);

  const hasHiddenItems = !showAllItems && procedures.length > INITIAL_VISIBLE_ITEMS;

  // O PDF leva a listagem inteira, e não o recorte visível: quem baixa espera o
  // relatório completo do período.
  const downloadProduction = useCallback(() => {
    buildClinicalProductionPdf({
      selectedDate,
      statusLabel: productionStatusLabels[selectedStatus],
      groupByLabel: groupByLabels[groupBy],
      summary,
      groups,
    });
  }, [selectedDate, selectedStatus, groupBy, summary, groups]);

  return {
    isLoading,
    error,
    refetch,
    selectedDate,
    setSelectedDate: changeSelectedDate,
    selectedStatus,
    setSelectedStatus: changeSelectedStatus,
    selectedProfessionalId,
    setSelectedProfessionalId: changeSelectedProfessionalId,
    groupBy,
    setGroupBy,
    summary,
    groups: visibleGroups,
    hasHiddenItems,
    showAllItems,
    setShowAllItems,
    downloadProduction,
  };
}
